import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { create } from 'zustand';

/**
 * Plan 07-05 — runtime FPS guard (logic only; DOM toast lives in
 * FpsGuardToast.tsx, imported alongside but kept in its own file so this
 * module stays JSX-free and ready to consume in any R3F-native context).
 *
 * Samples frame time every frame, tracks a rolling window of ~3 seconds, and
 * flips a Zustand flag when the median FPS drops below 40 for sustained 3s.
 * FpsGuardToast subscribes to the flag and renders a period-chrome warning
 * with a "Switch to Read the Tour" escape hatch.
 *
 * Architecture split (same pattern as DebugHUDPerfPanel):
 *   - <FpsGuardProbe />   — Canvas-scope (useFrame). Writes to useFpsStore.
 *                           Mount inside SceneGraph.
 *   - <FpsGuardToast />   — DOM (UIShell). Reads from useFpsStore.
 *                           Separate .tsx file to keep this module JSX-free.
 *
 * Thresholds:
 *   WINDOW_MS = 3000   — 3s rolling window (smooths 1-frame hitches)
 *   FPS_FLOOR = 40     — degraded threshold (well above 30 "unusable")
 *   MIN_SAMPLES = 30   — require ~half-second of data before alerting
 *
 * No per-frame re-renders: writes to the store only when degraded FLIPS or
 * the median moves by >2 fps — so the typical quiet-path cost is a compare.
 */

const WINDOW_MS = 3000;
const FPS_FLOOR = 40;
const MIN_SAMPLES = 30;

interface FpsSlice {
  degraded: boolean;
  medianFps: number;
  dismissed: boolean;
  set: (s: { degraded: boolean; medianFps: number }) => void;
  dismiss: () => void;
  reset: () => void;
}

export const useFpsStore = create<FpsSlice>((set) => ({
  degraded: false,
  medianFps: 60,
  dismissed: false,
  set: (s) =>
    set((prev) => ({
      // When recovering, clear any prior dismissal so a new degraded episode
      // can re-surface the toast instead of being silently filtered.
      dismissed: s.degraded ? prev.dismissed : false,
      degraded: s.degraded,
      medianFps: s.medianFps,
    })),
  dismiss: () => set({ dismissed: true }),
  reset: () => set({ degraded: false, medianFps: 60, dismissed: false }),
}));

/**
 * Canvas-scope probe. useFrame samples frame time; rolling median is written
 * to the store only when degraded flips or the median drifts >2 fps.
 */
export function FpsGuardProbe() {
  const samplesRef = useRef<Array<{ t: number; fps: number }>>([]);
  const lastFrameRef = useRef<number>(performance.now());
  const prevRef = useRef<{ degraded: boolean; medianFps: number }>({
    degraded: false,
    medianFps: 60,
  });
  const setStore = useFpsStore((s) => s.set);

  useFrame(() => {
    const now = performance.now();
    const dt = now - lastFrameRef.current;
    lastFrameRef.current = now;
    if (dt <= 0) return;
    const fps = 1000 / dt;

    samplesRef.current.push({ t: now, fps });
    samplesRef.current = samplesRef.current.filter((s) => now - s.t < WINDOW_MS);
    if (samplesRef.current.length < MIN_SAMPLES) return;

    const vals = samplesRef.current.map((s) => s.fps).sort((a, b) => a - b);
    const median = vals[Math.floor(vals.length / 2)];
    const degraded = median < FPS_FLOOR;

    const prev = prevRef.current;
    if (degraded !== prev.degraded || Math.abs(median - prev.medianFps) > 2) {
      prevRef.current = { degraded, medianFps: median };
      setStore({ degraded, medianFps: median });
    }
  });

  return null;
}
